import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, AlertCircle, Home, Phone, User } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';
import { useUzbekistanData } from '../lib/hooks/useUzbekistanData';
import YandexMapLocationPicker from '../components/YandexMapLocationPicker';


export default function BusinessSetup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { regions, getDistrictsByRegion, loading: dataLoading, error: dataError } = useUzbekistanData();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    businessName: '',
    regionId: '',
    districtId: '',
    address: '',
    phone: '+998',
    masterName: '',
    latitude: '',
    longitude: ''
  });

  const selectedRegion = regions.find(r => r.id.toString() === formData.regionId);
  const availableDistricts = selectedRegion ? getDistrictsByRegion(selectedRegion.id) : [];

  // Check if user already has a business profile
  useEffect(() => {
    const checkExistingProfile = async () => {
      if (!user) return;

      try {
        const { data: existingProfile, error } = await supabase
          .from('business_profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error checking existing profile:', error);
          return;
        }

        if (existingProfile) {
          // User already has a business profile, redirect to edit page
          navigate('/business/edit');
        }
      } catch (err) {
        console.error('Error checking business profile:', err);
      }
    };

    checkExistingProfile();
  }, [user, navigate]);

  const handleLocationSelect = (latitude: number, longitude: number) => {
    setFormData(prev => ({
      ...prev,
      latitude: latitude.toString(),
      longitude: longitude.toString()
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('No active session. Please sign in again.');
      }

      const selectedDistrict = availableDistricts.find(d => d.id.toString() === formData.districtId);
      const location = `${formData.address}, ${selectedDistrict?.name_uz || ''}, ${selectedRegion?.name_uz || ''}`;

      // Check if business profile already exists
      const { data: existingProfile, error: checkError } = await supabase
        .from('business_profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (checkError) {
        console.error('Check error:', checkError);
        throw checkError;
      }

      const profileData = {
        business_name: formData.businessName,
        region: selectedRegion?.name_uz || null,
        city: selectedDistrict?.name_uz || null,
        address: formData.address,
        location: location, // Keep for backward compatibility
        phone: formData.phone,
        master_name: formData.masterName || null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null
      };

      let error;
      if (existingProfile) {
        // Update existing profile
        const { error: updateError } = await supabase
          .from('business_profiles')
          .update(profileData)
          .eq('user_id', user.id);
        error = updateError;
      } else {
        // Insert new profile
        const { error: insertError } = await supabase
          .from('business_profiles')
          .insert({
            user_id: user.id,
            ...profileData
          });
        error = insertError;
      }

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      // Generate referral code for new profiles
      if (!existingProfile) {
        const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
        for (let attempt = 0; attempt < 5; attempt++) {
          const code = 'PM-' + Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
          const { error: codeError } = await supabase
            .from('business_profiles')
            .update({ referral_code: code })
            .eq('user_id', user.id);
          if (!codeError) break;
        }
      }

      // Apply pending referral code from registration
      const pendingCode = localStorage.getItem('pendingReferralCode');
      if (pendingCode) {
        localStorage.removeItem('pendingReferralCode');
        await supabase.rpc('apply_referral_code', {
          p_referred_user_id: user.id,
          p_code: pendingCode
        });
      }

      navigate('/');
    } catch (err) {
      console.error('Business setup error:', err);
      if (err instanceof Error && err.message.includes('PGRST301')) {
        setError('Authentication error. Please sign out and sign in again.');
      } else if (err instanceof Error && err.message.includes('duplicate key')) {
        setError('Sizda allaqachon biznes profili mavjud. Sahifani yangilang va qayta urinib ko\'ring.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save business profile');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <h2 className="text-center text-3xl font-extrabold text-gray-900">
              Biznesni sozlash
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Biznes ma'lumotlaringizni kiriting
            </p>
          </div>

          {(error || dataError) && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-700">{error || dataError}</p>
              </div>
            </div>
          )}

          {dataLoading && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <p className="text-blue-700">Viloyatlar ma'lumotlari yuklanmoqda...</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div>
              <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">
                Biznes nomi
              </label>
              <div className="mt-1 relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="businessName"
                  type="text"
                  required
                  value={formData.businessName}
                  onChange={(e) => setFormData(prev => ({ ...prev, businessName: e.target.value }))}
                  className="pl-10 pr-4 py-4 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                  placeholder="Masalan: Tech Repair Service"
                />
              </div>
            </div>

            <div>
              <label htmlFor="region" className="block text-sm font-medium text-gray-700">
                Viloyat
              </label>
              <div className="mt-1 relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  id="region"
                  required
                  value={formData.regionId}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    regionId: e.target.value,
                    districtId: '' // Reset district when region changes
                  }))}
                  className="pl-10 pr-10 py-4 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                  disabled={dataLoading}
                >
                  <option value="">Viloyatni tanlang</option>
                  {regions.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.name_uz}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                Shahar/Tuman
              </label>
              <div className="mt-1 relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  id="district"
                  required
                  value={formData.districtId}
                  onChange={(e) => setFormData(prev => ({ ...prev, districtId: e.target.value }))}
                  className="pl-10 pr-10 py-4 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                  style={{ backgroundColor: !formData.regionId ? '#f9fafb' : 'white' }}
                  disabled={!formData.regionId || dataLoading}
                >
                  <option value="">Shaharni tanlang</option>
                  {availableDistricts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {district.name_uz}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                Manzil
              </label>
              <div className="mt-1 relative">
                <Home className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="address"
                  type="text"
                  required
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="pl-10 pr-4 py-4 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                  placeholder="Ko'cha nomi va uy raqami"
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Telefon raqami
              </label>
              <div className="mt-1 relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (!value.startsWith('+998')) {
                      value = '+998' + value.replace(/^\+?998?/, '');
                    }
                    value = '+998' + value.slice(4).replace(/\D/g, '');
                    if (value.length > 13) {
                      value = value.slice(0, 13);
                    }
                    setFormData(prev => ({ ...prev, phone: value }));
                  }}
                  className="pl-10 pr-4 py-4 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                  placeholder="+998901234567"
                  maxLength={13}
                />
              </div>
            </div>

            <div>
              <label htmlFor="masterName" className="block text-sm font-medium text-gray-700">
                Master ismi (SMS uchun)
              </label>
              <div className="mt-1 relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="masterName"
                  type="text"
                  value={formData.masterName}
                  onChange={(e) => setFormData(prev => ({ ...prev, masterName: e.target.value }))}
                  className="pl-10 pr-4 py-4 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                  placeholder="Masalan: Qahramon"
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Bu ism SMS xabarlarida ko'rsatiladi
              </p>
            </div>

            {/* Interactive Map for Location Selection */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Joylashuvni tanlang (ixtiyoriy)
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Xaritada joylashuvni tanlang yoki joriy joylashuvni oling
              </p>
              
              <YandexMapLocationPicker
                latitude={formData.latitude ? parseFloat(formData.latitude) : null}
                longitude={formData.longitude ? parseFloat(formData.longitude) : null}
                onLocationSelect={handleLocationSelect}
                height="250px"
              />

              {/* Display current coordinates */}
              {(formData.latitude || formData.longitude) && (
                <div className="mt-3 p-2.5 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>Lat: <span className="font-mono text-gray-700">{formData.latitude ? parseFloat(formData.latitude).toFixed(6) : '—'}</span></span>
                    <span>Lng: <span className="font-mono text-gray-700">{formData.longitude ? parseFloat(formData.longitude).toFixed(6) : '—'}</span></span>
                  </div>
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saqlanmoqda...' : 'Biznesni saqlash'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}